import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { isStaffEmail } from '../auth/msal';
import { beginExternalSignIn } from '../auth/externalSso';
import { DiscoverResponse } from '../api/client';

// Email-first sign-in:
//   - Staff (@mountinsights.com)   → MSAL → /auth/callback.
//   - Client (SSO configured)      → external MSAL → /auth/external-callback.
//   - Client (password)            → email + password card on the same screen.
// /login/:slug skips the email prompt for a known client.

export default function Login() {
  const { slug } = useParams();
  const { signInStaff, signInClient, source, ready } = useAuth();

  const [email, setEmail] = useState('');
  const [discovered, setDiscovered] = useState<DiscoverResponse | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // /login/:slug — fetch the client's config once and either auto-start SSO
  // (when method=sso) or pre-select the client and show the password form.
  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const r = await fetch(`/api/auth/discover?slug=${encodeURIComponent(slug)}`);
        const data: DiscoverResponse = await r.json();
        setDiscovered(data);
        if (data.method === 'sso' && data.sso) {
          await beginExternalSignIn({
            tenantId: data.sso.tenant_id,
            appClientId: data.sso.app_client_id,
            slug: data.client?.slug ?? slug,
          });
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [slug]);

  if (ready && source) return <Navigate to="/" replace />;

  const onContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (isStaffEmail(email)) {
      await signInStaff();
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`/api/auth/discover?email=${encodeURIComponent(email.trim())}`);
      const data: DiscoverResponse = await r.json();
      setDiscovered(data);
      if (data.method === 'sso' && data.sso) {
        await beginExternalSignIn({
          tenantId: data.sso.tenant_id,
          appClientId: data.sso.app_client_id,
          slug: data.client?.slug ?? null,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signInClient(email, password);
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? errorMessage(err.message) : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  // Initial render for /login/:slug while we wait on /discover.
  if (slug && !discovered) {
    return (
      <Shell>
        <p style={{ color: 'var(--mi-muted)', margin: 0 }}>Looking up your workspace…</p>
      </Shell>
    );
  }

  // SSO sign-in is in progress (loginRedirect is about to navigate away).
  if (discovered?.method === 'sso') {
    return (
      <Shell>
        <p style={{ color: 'var(--mi-muted)', margin: 0 }}>Redirecting to Microsoft to sign in…</p>
      </Shell>
    );
  }

  // After /discover we know which form to show; before, ask for email.
  // 'either' shows the SSO button above the password form.
  const showSsoButton = discovered?.method === 'either' && discovered.sso;
  const showPasswordForm = !!discovered;
  const showEmailForm = !discovered;

  return (
    <Shell>
      <p style={{ color: 'var(--mi-muted)', marginTop: 0 }}>
        {discovered?.client ? `Sign in to ${discovered.client.name}.` : 'Sign in to your client portal.'}
      </p>

      {showEmailForm && (
        <form onSubmit={onContinue}>
          <label style={{ display: 'block', marginBottom: 12 }}>
            Email
            <input
              type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
          </label>
          {error && <div style={errorStyle}>{error}</div>}
          <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={busy || !email.trim()}>
            {busy ? 'Working…' : 'Continue'}
          </button>
        </form>
      )}

      {showPasswordForm && (
        <>
          {showSsoButton && discovered?.sso && (
            <>
              <button
                type="button"
                className="btn-accent"
                style={{ width: '100%' }}
                onClick={() => beginExternalSignIn({
                  tenantId: discovered.sso!.tenant_id,
                  appClientId: discovered.sso!.app_client_id,
                  slug: discovered.client?.slug ?? null,
                })}
              >
                {discovered.sso.display_name}
              </button>
              <div style={{ textAlign: 'center', color: 'var(--mi-muted)', margin: '10px 0', fontSize: 12 }}>or sign in with a password</div>
            </>
          )}
          <form onSubmit={onPassword}>
            <label style={{ display: 'block', marginBottom: 12 }}>
              Email
              <input
                type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'block', marginBottom: 12 }}>
              Password
              <input
                type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
              />
            </label>
            {error && <div style={errorStyle}>{error}</div>}
            <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--mi-bg)' }}>
      <div className="card" style={{ width: 380 }}>
        <img src="/MI-Logo-LightBackground.png" alt="Mount Insights" style={{ width: '100%', maxWidth: 150, height: 'auto', marginTop: 0, marginBottom: 12, display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
        {children}
      </div>
    </div>
  );
}

function errorMessage(code: string): string {
  switch (code) {
    case 'invalid_credentials': return 'Incorrect email or password.';
    case 'account_locked': return 'Too many attempts. Try again in 15 minutes.';
    case 'invalid': return 'Please enter a valid email and password.';
    default: return 'Sign-in failed.';
  }
}

const inputStyle: React.CSSProperties = { width: '100%', padding: '0.5rem', marginTop: 4, borderRadius: 6, border: '1px solid var(--mi-border)' };
const errorStyle: React.CSSProperties = { background: '#fee', color: '#900', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 14 };
